import { Layout, Table, Typography } from "antd";
import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from "react";
import axios from "api/axios";
import { useDpatIndicatorDataSource } from "hooks/useDpatIndicatorDataSource";
import DpatSubIndicatorSection from "../setup/DpatSubIndicatorSection";
import DpatIndicatorCmsIntro from "../setup/DpatIndicatorCmsIntro";
import MaxScoreDisplay from "../setup/MaxScoreDisplay";
import ActualScoreDisplay from "../setup/ActualScoreDisplay";
import IndicatorScoreSection from "../setup/IndicatorScoreSection";
import Comment from "components/Comments";
import { calculatePercentage, formatDataGeneral, getAttributeValue } from "utils/utils";
import { buildSearchableColumns, getDpatTablePagination } from "utils/dpatSearchableTable";

const SCHOOL_PROGRAM = "g27TeeehRQC";
const SCHOOL_PERIODIC_STAGE = "z6mWkIfypaw";
const TOILET_ELEMENT = "NLFmA25boNu";
const WATER_ELEMENT = "JrAtSHckaq4";
const MALE_ENROLLMENT_ELEMENT = "W7cDb7KYNXq";
const FEMALE_ENROLLMENT_ELEMENT = "Fzivg13nELP";

function resolveBooleanYesNo(dataValues, elementId) {
  const item = dataValues?.find((value) => value.dataElement === elementId);
  if (!item) {
    return "NO";
  }
  return item.value === "true" ? "YES" : "NO";
}

function resolveEnrollmentValue(dataValues, elementId) {
  const item = dataValues?.find((value) => value.dataElement === elementId);
  if (!item || item.value === "" || item.value == null) {
    return 0;
  }
  const parsed = parseFloat(item.value);
  return Number.isNaN(parsed) ? 0 : parsed;
}

const PublicSchoolFacility = forwardRef(({
  year,
  districtId,
  hideComment,
  savedAssessmentData,
  assessmentStartLoading,
  isAssessmentStarted,
}, ref) => {
  const [score, setScore] = useState(0);
  const [summary, setSummary] = useState([]);
  const [schoolList, setSchoolList] = useState([]);
  const [schoolTotal, setSchoolTotal] = useState(0);
  const [toiletCount, setToiletCount] = useState(0);
  const [waterCount, setWaterCount] = useState(0);
  const [bothCount, setBothCount] = useState(0);
  const [toiletPercentage, setToiletPercentage] = useState(0);
  const [waterPercentage, setWaterPercentage] = useState(0);
  const [bothPercentage, setBothPercentage] = useState(0);
  const [maxScore, setMaxScore] = useState(2);

  const { Content } = Layout;
  const { Title } = Typography;

  useImperativeHandle(ref, () => ({
    getData: () => ({
      indicator: "SDI5",
      area: "Environmental Health, Sanitation and Climate Action",
      maxScore,
      data: summary,
      schoolList,
      score,
      percentageData: bothPercentage,
      schoolTotal,
      toiletCount,
      waterCount,
      bothCount,
      toiletPercentage,
      waterPercentage,
      bothPercentage,
    }),
  }));

  const DPAT_SLUG = "sdi5.0-5.4";

  const loadFromDhis2 = useCallback(() => {
    axios
      .get(`/tracker/trackedEntities?orgUnit=${districtId}&program=${SCHOOL_PROGRAM}&startDate=${year}-01-01&endDate=${year}-12-31&pageSize=5000`)
      .then((result) => {
        if (result.data.instances.length > 0) {
          axios
            .get(`/tracker/events?program=${SCHOOL_PROGRAM}&orgUnit=${districtId}&startDate=${year}-01-01&endDate=${year}-12-31&pageSize=5000`)
            .then((resp) => {
              const schools = formatDataGeneral(result.data.instances, "Type", "Public") || [];
              const reports = resp.data.instances;
              const totalSchools = schools.length;
              let withToilet = 0;
              let withWater = 0;
              let withBoth = 0;
              const schoolRows = [];

              schools.forEach((school, idx) => {
                const currentReport = reports.find(
                  (report) => report.trackedEntity === school.trackedEntity
                    && report.programStage === SCHOOL_PERIODIC_STAGE,
                );
                const dataValues = currentReport?.dataValues || [];
                const hasToilet = resolveBooleanYesNo(dataValues, TOILET_ELEMENT) === "YES";
                const hasWater = resolveBooleanYesNo(dataValues, WATER_ELEMENT) === "YES";
                const maleEnrollment = resolveEnrollmentValue(dataValues, MALE_ENROLLMENT_ELEMENT);
                const femaleEnrollment = resolveEnrollmentValue(dataValues, FEMALE_ENROLLMENT_ELEMENT);

                if (hasToilet) {
                  withToilet++;
                }
                if (hasWater) {
                  withWater++;
                }
                if (hasToilet && hasWater) {
                  withBoth++;
                }

                schoolRows.push({
                  key: school.trackedEntity || `school-${idx}`,
                  no: idx + 1,
                  school: getAttributeValue("Name of School", school),
                  community: getAttributeValue("Community", school),
                  maleEnrollment,
                  femaleEnrollment,
                  totalEnrollment: maleEnrollment + femaleEnrollment,
                  toilet: hasToilet ? "YES" : "NO",
                  water: hasWater ? "YES" : "NO",
                });
              });

              const toiletPct = Number(calculatePercentage(withToilet, totalSchools).toFixed(2));
              const waterPct = Number(calculatePercentage(withWater, totalSchools).toFixed(2));
              const bothPct = Number(calculatePercentage(withBoth, totalSchools).toFixed(2));

              setSchoolTotal(totalSchools);
              setToiletCount(withToilet);
              setWaterCount(withWater);
              setBothCount(withBoth);
              setToiletPercentage(toiletPct);
              setWaterPercentage(waterPct);
              setBothPercentage(bothPct);
              setSchoolList(schoolRows);
              setSummary([{
                key: "public-school-summary",
                schoolTotal: totalSchools,
                totalFacility: withBoth,
                percentage: bothPct.toFixed(2),
              }]);

              if (bothPct >= 90) {
                setScore(2);
              } else {
                setScore(0);
              }
            })
            .catch((err) => console.log(err));
        } else {
          setSchoolTotal(0);
          setToiletCount(0);
          setWaterCount(0);
          setBothCount(0);
          setToiletPercentage(0);
          setWaterPercentage(0);
          setBothPercentage(0);
          setSchoolList([]);
          setSummary([]);
          setScore(0);
        }
      })
      .catch((err) => console.log(err));
  }, [districtId, year]);

  useDpatIndicatorDataSource({
    isAssessmentStarted,
    savedAssessmentData,
    assessmentStartLoading,
    loadFromDhis2,
    applySnapshot: (snap) => {
      if (snap.data) setSummary(snap.data);
      if (snap.schoolList) setSchoolList(snap.schoolList);
      if (snap.score != null) setScore(snap.score);
      if (snap.percentageData != null) setBothPercentage(snap.percentageData);
      if (snap.schoolTotal != null) setSchoolTotal(snap.schoolTotal);
      if (snap.toiletCount != null) setToiletCount(snap.toiletCount);
      if (snap.waterCount != null) setWaterCount(snap.waterCount);
      if (snap.bothCount != null) setBothCount(snap.bothCount);
      if (snap.toiletPercentage != null) setToiletPercentage(snap.toiletPercentage);
      if (snap.waterPercentage != null) setWaterPercentage(snap.waterPercentage);
      if (snap.bothPercentage != null) setBothPercentage(snap.bothPercentage);
      if (snap.maxScore != null) setMaxScore(snap.maxScore);
    },
    deps: [year, districtId, loadFromDhis2],
  });

  const summaryColumns = [
    {
      title: "No. of public schools in the District",
      dataIndex: "schoolTotal",
      key: "schoolTotal",
    },
    {
      title: "No. of public schools with functional institutional Toilet and water facilities",
      dataIndex: "totalFacility",
      key: "totalFacility",
    },
    {
      title: "% of public schools with institutional Toilet and water facilities",
      dataIndex: "percentage",
      key: "percentage",
    },
  ];

  const schoolListColumns = useMemo(
    () => buildSearchableColumns(
      [
        { title: "No.", dataIndex: "no", key: "no" },
        { title: "School Name", dataIndex: "school", key: "school" },
        { title: "Community", dataIndex: "community", key: "community" },
        { title: "Male Enrollment", dataIndex: "maleEnrollment", key: "maleEnrollment" },
        { title: "Female Enrollment", dataIndex: "femaleEnrollment", key: "femaleEnrollment" },
        { title: "Total Enrollment", dataIndex: "totalEnrollment", key: "totalEnrollment" },
        { title: "Functional Toilet Facility (Yes/No)", dataIndex: "toilet", key: "toilet" },
        { title: "Functional Water Facility (Yes/No)", dataIndex: "water", key: "water" },
      ],
      schoolList,
      {
        textSearchKeys: ["school", "community"],
        selectFilterKeys: ["toilet", "water"],
      },
    ),
    [schoolList],
  );

  return (
    <Comment
      data={summary}
      year={year}
      districtId={districtId}
      tableCommentedId={`sdi5.0-5.4-${year}`}
      hideComment={hideComment}
    >
      {({ renderCommentFeedbackFooter }) => (
        <DpatSubIndicatorSection code={DPAT_SLUG}>
          <DpatIndicatorCmsIntro
            year={year}
            slug={DPAT_SLUG}
            mainTitleStyle={{ marginTop: "30px" }}
          />

          <IndicatorScoreSection>
            <MaxScoreDisplay score={maxScore} />
            <ActualScoreDisplay slug={DPAT_SLUG} label="SDI 5.0-5.4 Actual Score:" score={score} />
          </IndicatorScoreSection>

          <Title level={4} style={{ marginTop: "20px" }}>
            Evidence of functional toilet and water facilities in public schools
          </Title>
          <Table
            columns={summaryColumns}
            dataSource={summary || []}
            pagination={false}
            bordered
            rowKey="key"
          />

          <Title level={4} style={{ marginTop: "20px" }}>
            GES Report List of Public Schools (School Profile Tracker)
          </Title>
          <Table
            columns={schoolListColumns}
            dataSource={schoolList}
            pagination={getDpatTablePagination("schools")}
            bordered
            rowKey="key"
          />

          <Title level={5} style={{ marginTop: "30px" }}>Conclusion</Title>
          <Content>
            <div>The District has {schoolTotal} public schools in {year}.</div>
            <div>
              {toiletCount} ({toiletPercentage}%) public schools have functional institutional toilet facilities.
            </div>
            <div>
              {waterCount} ({waterPercentage}%) public schools have functional institutional water facilities.
            </div>
            <div>
              {bothCount} ({bothPercentage}%) public schools have both functional institutional toilet and water facilities.
            </div>
          </Content>
          {!hideComment && renderCommentFeedbackFooter()}
        </DpatSubIndicatorSection>
      )}
    </Comment>
  );
});

export default PublicSchoolFacility;
